/**
 * Mock 模块：heart_voyage_gift_banner
 * 默认正式已绑 · 阶段卡对齐 state-stage.png（28000/30000）。
 * Preview `?state=` 切未绑 / 海选 / 决赛 / 未上榜 / 隐藏。
 * @see mockMd/heart_voyage_gift_banner.md
 */
import {
  storiesAvatar,
  storiesAvatarPeer,
} from '../components/GiftBanner/stories-images'
import {
  RelationType,
  type ActivityMemberV2,
  type HeartVoyageDailyBannerBlock,
  type HeartVoyageGiftBannerData,
  type HeartVoyagePeakBannerBlock,
  type HeartVoyageStageBannerBlock,
} from '../types'

const MOCK_USER_AVATAR = storiesAvatar
const MOCK_PEER_AVATAR = storiesAvatarPeer

const mockMember = (
  id: number,
  name: string,
  avatar: string,
  gender: number
): ActivityMemberV2 => ({
  id,
  name,
  zy_id: String(id),
  avatar,
  gender,
})

const memberA = mockMember(10001, 'Alice', MOCK_USER_AVATAR, 2)
const memberB = mockMember(10002, 'Bob', MOCK_PEER_AVATAR, 1)

const emptyStageBanner: HeartVoyageStageBannerBlock = {
  member_a: null,
  member_b: null,
  rel_type: 0 as RelationType,
  ship_level: 0,
  affinity: 0,
  need_affinity: 0,
}

const emptyPeakBanner: HeartVoyagePeakBannerBlock = {
  member_a: null,
  member_b: null,
  rel_type: 0 as RelationType,
  segment: 0,
  group_id: 0,
  group_name: '',
  rank: 0,
  score: 0,
  score_gap: 0,
  segment_countdown: 0,
}

/** 未绑 / 不可参赛时 daily_banner 为 null，对应 JSON null */
const emptyDailyBanner = null

const stageBanner: HeartVoyageStageBannerBlock = {
  member_a: memberA,
  member_b: memberB,
  rel_type: RelationType.Couple,
  ship_level: 3,
  affinity: 28000,
  need_affinity: 30000,
}

const peakBanner = (
  segment: number,
  rank: number,
  groupId: number,
  groupName: string,
  scoreGap: number
): HeartVoyagePeakBannerBlock => ({
  member_a: memberA,
  member_b: memberB,
  rel_type: RelationType.Couple,
  segment,
  group_id: groupId,
  group_name: groupName,
  rank,
  score: 19999,
  score_gap: scoreGap,
  segment_countdown: 3600,
})

const dailyBanner = (
  round: number,
  pairAffinity: number,
  threshold: number,
  roundCountdown = 3600
): HeartVoyageDailyBannerBlock => ({
  member_a: memberA,
  member_b: memberB,
  rel_type: RelationType.Couple,
  round,
  pair_affinity: pairAffinity,
  threshold,
  round_countdown: roundCountdown,
})

const baseBound = {
  err_msg: '',
  show: true,
  activity_id: 12345,
  url: 'https://example.com/activity/heart_voyage',
  has_relation: true,
  pair_key: '10001:10002',
  daily_can_join: true,
  add_affinity: 0,
  add_score: 0,
  add_pair_affinity: 0,
  self_member: null,
  guide_text: '',
  has_empty_slot: false,
  coin_reward: 0,
  coin_reward_icon: '',
  stage_banner: stageBanner,
}

/** 默认 · 已绑阶段卡（peak 仍返回，Preview 停在第一张） */
export const HeartVoyageGiftBannerMock: HeartVoyageGiftBannerData = {
  ...baseBound,
  peak_banner: peakBanner(1, 10, 0, 'Pinnacle', 19999),
  daily_banner: dailyBanner(2, 2200, 2000),
}

/** 正式未绑 · 对齐 state-unbound.png */
export const HeartVoyageGiftBannerUnboundMock: HeartVoyageGiftBannerData = {
  err_msg: '',
  show: true,
  activity_id: 12345,
  url: 'https://example.com/activity/heart_voyage',
  has_relation: false,
  pair_key: '',
  daily_can_join: false,
  add_affinity: 0,
  add_score: 0,
  add_pair_affinity: 0,
  self_member: memberA,
  guide_text: '',
  has_empty_slot: true,
  coin_reward: 1000,
  coin_reward_icon: '',
  stage_banner: emptyStageBanner,
  peak_banner: emptyPeakBanner,
  daily_banner: emptyDailyBanner,
}

/** 海选常态 · 对齐 state-peak-qualifier.png */
export const HeartVoyageGiftBannerQualifierMock: HeartVoyageGiftBannerData = {
  ...baseBound,
  peak_banner: peakBanner(1, 10, 0, 'Pinnacle', 19999),
  daily_banner: dailyBanner(2, 2200, 2000),
}

/** 决赛常态 · 对齐 state-peak-final.png */
export const HeartVoyageGiftBannerFinalsMock: HeartVoyageGiftBannerData = {
  ...baseBound,
  peak_banner: peakBanner(2, 112, 10, '', 19999),
  daily_banner: dailyBanner(2, 2200, 2000),
}

/** 未上榜 rank=0 → UI `99+` */
export const HeartVoyageGiftBannerUnrankedMock: HeartVoyageGiftBannerData = {
  ...baseBound,
  peak_banner: peakBanner(1, 0, 0, 'Pinnacle', 19999),
  daily_banner: dailyBanner(2, 2200, 2000),
}

/** 非正式期 · 不出 */
export const HeartVoyageGiftBannerHiddenMock: HeartVoyageGiftBannerData = {
  err_msg: '',
  show: false,
  activity_id: 0,
  url: '',
  has_relation: false,
  pair_key: '',
  daily_can_join: false,
  add_affinity: 0,
  add_score: 0,
  add_pair_affinity: 0,
  self_member: null,
  guide_text: '',
  has_empty_slot: false,
  coin_reward: 0,
  coin_reward_icon: '',
  stage_banner: emptyStageBanner,
  peak_banner: emptyPeakBanner,
  daily_banner: emptyDailyBanner,
}

/** 已绑但每日不可参赛（淘汰/无分组）· daily_banner=null */
export const HeartVoyageGiftBannerDailyDisabledMock: HeartVoyageGiftBannerData =
  {
    ...baseBound,
    daily_can_join: false,
    peak_banner: peakBanner(1, 10, 0, 'Pinnacle', 19999),
    daily_banner: emptyDailyBanner,
  }

/** 海选数值变更 · 9→10 / 19900→19999（+1 / +99） */
export const HeartVoyageGiftBannerQualifierDiffMock: HeartVoyageGiftBannerData =
  {
    ...baseBound,
    add_affinity: 100,
    add_score: 99,
    add_pair_affinity: 50,
    peak_banner: peakBanner(1, 10, 0, 'Pinnacle', 19999),
    daily_banner: dailyBanner(2, 2250, 2000),
  }

/** 决赛数值变更 · 112→111 / 19999→19900（-1 / -99） */
export const HeartVoyageGiftBannerFinalsDiffMock: HeartVoyageGiftBannerData = {
  ...baseBound,
  add_affinity: -100,
  add_score: -99,
  add_pair_affinity: -50,
  peak_banner: peakBanner(2, 111, 10, '', 19900),
  daily_banner: dailyBanner(2, 2150, 2000),
}
